import { useContext, useState } from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { ArrowBack } from "@remo-co/ui-core/src/icons/ArrowBack";
import { CUSTOM_FLOOR_CONTEXT } from "modules/customFloorPlan/context/useCustomFloorPlan";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isInPostTrialPlan } from "modules/companyPlanSettings/utils/trial";
import { CustomFloorPlanUpload } from "modules/customFloorPlan/settings/CustomFloorPlanUpload";
import { useAppDispatch } from "store/hooks";
import {
  addDialogNotification,
  closeDialogNotification,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { Container } from "@remo-co/ui-core/src/components/Container";
import CustomFloorPlanContainer from "@remo-co/client/src/modules/customFloorPlan/CustomFloorPlanContainer/CustomFloorPlanContainer";
import { styles } from "./styles";

const CustomFloorPlans = (): JSX.Element => {
  const { t } = useI18n(["common", "customFloorPlan"]);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const { templates, getTemplates, isLoading } =
    useContext(CUSTOM_FLOOR_CONTEXT);
  const [isUploaderVisible, setUploaderVisible] = useState(false);
  const [isDialogVisible, setDialogVisible] = useState(false);
  const dispatch = useAppDispatch();

  const onSaveHandler = async () => {
    setUploaderVisible(false);
    await getTemplates();
  };

  const showUploader = () => {
    setUploaderVisible(true);
  };

  const hideUploader = () => {
    if (isDialogVisible) {
      dispatch(
        addDialogNotification({
          title: t("customFloorPlan:cancel.floor.plan.upload"),
          message: t("customFloorPlan:leave.without.saving"),
          confirmText: t("customFloorPlan:dont.save"),
          dismissText: t("customFloorPlan:cancel"),
          closeIconStyles: styles.closeIcon,
          contentStyles: styles.content,
          actionStyles: styles.actionButtons,
          onConfirm: () => {
            setUploaderVisible(false);
            setDialogVisible(false);
          },
          onDismiss: () => {
            dispatch(closeDialogNotification());
          },
        }),
      );
    } else {
      setUploaderVisible(false);
    }
  };

  if (isLoading) {
    return <PageLoader message={t("loading")} />;
  }

  return (
    <Container sx={styles.wrapper} data-testid="cfp-settings-container">
      {isUploaderVisible && (
        <>
          <Button
            onClick={hideUploader}
            variant="text"
            color="dark"
            className="custom-floor-plan-back-btn"
            data-testid="custom-floor-plan-back-btn"
          >
            <span className="material-icons-outlined">
              <ArrowBack />
            </span>
            {t("customFloorPlan:back.to.floor.plans")}
          </Button>
          <CustomFloorPlanContainer
            onSave={onSaveHandler}
            templates={templates?.privateTemplates}
            handleSetDialogVisible={setDialogVisible}
          />
        </>
      )}
      {!isUploaderVisible && (
        <>
          <div className="custom-floor-plan-title">
            <Typography variant="h3">
              {t("settings:custom.floor.plans")}
            </Typography>
          </div>
          <CustomFloorPlanUpload
            isAccountSettings
            templates={templates?.privateTemplates || []}
            showUploader={showUploader}
            disabled={isInPostTrialPlan(myCompanyPlan)}
          />
        </>
      )}
    </Container>
  );
};

export default CustomFloorPlans;
